import {useQuery, type UseQueryOptions} from '@tanstack/react-query'
import {
    fetchLatestFleetThreatScore,
    fetchFleetThreatScore,
    fetchLatestLocationThreatScores,
    fetchLocationThreatScore,
    fetchLocationTagThreatScore,
    fetchLocationThreatScoresByTags,
} from '../../api/warning-and-scoring/warning-and-scoring'
import type {
    ThreatScorePoint,
    ThreatScorePeriod,
    FleetThreatScoreSeries,
    LocationThreatScoreSeries,
    LatestThreatScoresByLocation,
    ThreatScoresByTag,
} from '../../api/warning-and-scoring/types/warning-and-scoring.types'
import {threatScoreKeys} from './keys'
import type {GuidType} from '../../values/generic-type-definitions'
import type {AxiosError} from 'axios'

/**
 * warning-and-scoring caches score results server-side for 5 minutes
 * (chart/templates/warning-and-scoring-api-configmap.yaml -> scoreCacheLifespan).
 * Polling faster than that returns byte-identical data, so we poll at 2 minutes:
 * often enough to observe every cache generation shortly after it appears,
 * without the 30x redundant traffic the old 10s WebSocket schedule produced.
 */
export const THREAT_SCORE_POLL_MS = 2 * 60 * 1000

const pollingDefaults = {
    refetchInterval: THREAT_SCORE_POLL_MS,
    staleTime: THREAT_SCORE_POLL_MS,
}

export function useQueryLatestFleetThreatScore(
    options?: UseQueryOptions<ThreatScorePoint, AxiosError>,
) {
    return useQuery<ThreatScorePoint, AxiosError>({
        queryKey: threatScoreKeys.latestFleet(),
        queryFn: () => fetchLatestFleetThreatScore(),
        ...pollingDefaults,
        ...options,
    })
}

export function useQueryFleetThreatScore(
    period: ThreatScorePeriod,
    options?: UseQueryOptions<FleetThreatScoreSeries, AxiosError>,
) {
    return useQuery<FleetThreatScoreSeries, AxiosError>({
        queryKey: threatScoreKeys.fleetTrend(period),
        queryFn: () => fetchFleetThreatScore(period),
        ...pollingDefaults,
        ...options,
    })
}

export function useQueryLatestLocationThreatScores(
    options?: UseQueryOptions<LatestThreatScoresByLocation, AxiosError>,
) {
    return useQuery<LatestThreatScoresByLocation, AxiosError>({
        queryKey: threatScoreKeys.latestLocations(),
        queryFn: () => fetchLatestLocationThreatScores(),
        ...pollingDefaults,
        ...options,
    })
}

export function useQueryLocationThreatScore(
    period: ThreatScorePeriod,
    location: GuidType | null | undefined,
    options?: UseQueryOptions<LocationThreatScoreSeries, AxiosError>,
) {
    return useQuery<LocationThreatScoreSeries, AxiosError>({
        queryKey: threatScoreKeys.locationTrend(period, location ?? ''),
        queryFn: () => fetchLocationThreatScore(period, location as GuidType),
        ...pollingDefaults,
        ...options,
        // After `options`, not before. The `location as GuidType` cast above is
        // only safe because of this guard, so a caller passing its own `enabled`
        // must not be able to switch it off - it would fetch with an empty
        // location. A caller can still disable the query, it just cannot enable
        // one that has nothing to ask for.
        enabled: Boolean(location) && (options?.enabled ?? true),
    })
}

export function useQueryLocationThreatScoresByTags(
    period: ThreatScorePeriod,
    tags: GuidType[],
    options?: UseQueryOptions<ThreatScoresByTag, AxiosError>,
) {
    return useQuery<ThreatScoresByTag, AxiosError>({
        queryKey: threatScoreKeys.scoresByTags(period, tags),
        queryFn: () => fetchLocationThreatScoresByTags(period, tags),
        ...pollingDefaults,
        ...options,
    })
}

export function useQueryLocationTagThreatScore(
    period: ThreatScorePeriod,
    tag: string | null | undefined,
    options?: UseQueryOptions<LocationThreatScoreSeries, AxiosError>,
) {
    return useQuery<LocationThreatScoreSeries, AxiosError>({
        queryKey: threatScoreKeys.locationTagTrend(period, tag ?? ''),
        queryFn: () => fetchLocationTagThreatScore(period, tag as string),
        ...pollingDefaults,
        ...options,
        // After `options`. Same reason as useQueryLocationThreatScore above.
        enabled: Boolean(tag) && (options?.enabled ?? true),
    })
}
